import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { homePathForUser, useAuth } from "./AuthContext";
import { isRefereeAccount } from "./referee";
import { canPlayAsAccount } from "./accountMode";
import type { RoleCode } from "../lib/api";

export default function RequireRole({
  roles,
  allowPlayerProfile = false,
  children,
}: {
  roles: RoleCode[];
  /** También deja pasar a la cuenta que opera un perfil de jugador aunque no
   * tenga el rol (organizador que juega, Eduardo 2026-10-04). El servidor
   * sigue filtrando todo por los perfiles de la cuenta. */
  allowPlayerProfile?: boolean;
  children: ReactNode;
}) {
  const { status, user, isImpersonating } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <section className="card">
        <p className="muted">Cargando sesión…</p>
      </section>
    );
  }

  if (status === "anonymous" || !user) {
    // `next` con ruta + query: LoginPage lo honra tras entrar. Antes se pasaba
    // `state.from` (que LoginPage ignoraba) y sin el query string, así que el
    // regreso del OAuth de Mercado Pago (/admin/payments/mp-return?code&state)
    // se perdía si la sesión no estaba viva al volver (2026-09-04, Luis).
    // También el ancla (2026-09-30): el recordatorio «Documentos pendientes»
    // llega con `#documentos` y, sin ella, tras entrar la página abría arriba
    // en vez de en la tarjeta para subirlos.
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  const allowed =
    roles.some((role) => user.roles.includes(role)) || (allowPlayerProfile && canPlayAsAccount(user));
  if (!allowed) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  // Gate global: mientras la bandera siga encendida (temporal recién
  // emitida o reset pendiente de completar), toda ruta autenticada
  // redirige a "Crea tu contraseña" — RequireRole envuelve todas las
  // rutas protegidas, así que este es el único punto que hace falta tocar.
  // EXCEPTO en impersonación: el admin que entra "como jugador" tras dar
  // Bienvenida NO debe aterrizar en el formulario (pisaría la temporal que
  // el jugador recibió por correo; el backend además lo rechaza con 403).
  // El árbitro no entra al panel del organizador (salvo sus avisos): su
  // pantalla es «Mis partidos».
  if (
    isRefereeAccount(user) &&
    location.pathname.startsWith("/admin") &&
    location.pathname !== "/admin/notifications" &&
    !user.must_change_password
  ) {
    return <Navigate to="/arbitro" replace />;
  }

  if (
    user.must_change_password &&
    !isImpersonating &&
    location.pathname !== "/cuenta/contrasena"
  ) {
    return <Navigate to="/cuenta/contrasena" replace />;
  }

  return <>{children}</>;
}
